
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";
import vSelect3 from "vselect3";
import { UserPermissions } from "@/store/types";
import SchedulesCalendar from "@/components/schedules/SchedulesCalendar.vue";
import ScheduleModal from "@/components/schedules/ScheduleModal.vue";
import { ScheduleType } from "@/store/schedules/types";
import { ScheduleTypes } from "@/components/schedules/ScheduleEnums";
import ValidatePinCodeModal from "@/components/ValidatePinCodeModal.vue";
import AreaNotesForm from "@/components/area-notes/AreaNotesForm.vue";
import AreaNotesTable from "@/components/area-notes/AreaNotesTable.vue";
import { EventDetails } from "@/store/site-monitor/types";
import AreaEventHistory from "@/components/area-event-history/AreaEventHistory.vue";
import { CustomFieldDto, CustomFieldTableTypes } from "@/store/custom-fields/types";
import { truncateString } from "@/filters";
import AreaContacts from "@/components/contacts/AreaContacts.vue";
import CustomDefinedFieldsDisplay from "@/components/custom-fields/CustomDefinedFieldsDisplay.vue";
import HyperlinkContact from "@/components/area-details/HyperlinkContact.vue";
import AreaDetailsPopoutButton from "@/components/area-details/AreaDetailsPopoutButton.vue";

const SiteMonitor = namespace("siteMonitor");
const Schedules = namespace("schedules");
const CustomFields = namespace("customFields");

@Component({
	components: {
		"v-select-3": vSelect3,
		"schedules-calendar": SchedulesCalendar,
		"schedule-modal": ScheduleModal,
		"area-notes-form": AreaNotesForm,
		"area-notes-table": AreaNotesTable,
		"area-event-history-tab": AreaEventHistory,
		"validate-pin-code-modal": ValidatePinCodeModal,
		"custom-fields-display": CustomDefinedFieldsDisplay,
		"area-contacts": AreaContacts,
		"hyperlink-contact": HyperlinkContact,
		"area-details-popout": AreaDetailsPopoutButton
	},
	filters: { truncateString }
})
export default class AreaDetails extends Vue {
	@SiteMonitor.Mutation setEventDetailsShown: any;
	@SiteMonitor.Action loadEventGroup: any;
	@SiteMonitor.State areaDetailsTabIndex: number;
	@SiteMonitor.Getter("getEventDetails") private eventDetails: EventDetails;

	@Schedules.State selectedGroup: number | null;
	@Schedules.State scheduleTypes: ScheduleType[];
	@Schedules.State selectedScheduleTypes: number[];
	@Schedules.Action loadScheduleTypes: () => Promise<void>;

	@Schedules.Mutation setSelectedGroup: (selectedGroup: number) => void;
	@Schedules.Mutation setEnabledScheduleFilter: (disabled: boolean | null) => void;
	@Schedules.Mutation updateSearchString: (searchString: string) => void;
	@Schedules.Mutation addSelectedScheduleType: (scheduleTypeId: number) => void;
	@Schedules.Mutation removeSelectedScheduleType: (scheduleTypeId: number) => void;
	@Schedules.Mutation setIsReloadRequired: (isReloadRequired: boolean) => void;
	@Schedules.Mutation resetSelectedGroup: () => void;

	@Schedules.Getter isScheduleTypeSelected: (scheduleTypeId: number) => boolean;

	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;
	@Getter("getPermissions") permissions: UserPermissions;

	public eventAreaDetails: any = {};
	private isLoading: boolean = true;

	private enabledScheduleFilter: boolean = true;
	private scheduleSearchFilter: string = "";
	private scheduleCollapseOpen: boolean = false;
	private selectedScheduleTypeOptions: number[] = [];
	private readonly areaCustomFieldType = CustomFieldTableTypes.Area;

	private readonly customFieldGets: { tableTypeId: CustomFieldTableTypes }[] = [
		{ tableTypeId: CustomFieldTableTypes.Area },
		{ tableTypeId: CustomFieldTableTypes.Contact },
	];

	@Prop({ type: Boolean, default: false }) showTitle: boolean;
	@Prop({ type: Number, default: false }) groupId: number;
	@Prop({ type: Boolean, default: false }) windowed: boolean;
	@Prop({ type: Boolean, default: false }) autoPopulate: boolean;

	@CustomFields.Action retrieveCustomFields: ({ tableType, live }) => Promise<void>;
	@CustomFields.State areaCustomFields: CustomFieldDto[];

	private async mounted(): Promise<void> {
		if (this.isCustomFieldsEnabled) {
			this.customFieldGets.forEach(async (cf) => {
				await this.updateCustomFields(cf.tableTypeId);
			});
		}

		await this.loadEventAreaDetails();

		this.selectedScheduleTypeOptions = this.selectedScheduleTypes;

		if (!this.isMaskingEnabled && this.selectedScheduleTypeOptions.includes(ScheduleTypes.Masking)) {
			this.selectedScheduleTypeOptions.removeWhere(s => s == ScheduleTypes.Masking);
		}

		if (!this.isEventRaiseEnabled && this.selectedScheduleTypeOptions.includes(ScheduleTypes.EventRaise)) {
			this.selectedScheduleTypeOptions.removeWhere(s => s == ScheduleTypes.EventRaise);
		}

		if (this.showSchedules) {
			await this.loadSchedules();
		}

		this.isLoading = false;
	}

	beforeDestroy(): void {
		this.resetSelectedGroup();
	}

	private async loadEventAreaDetails(): Promise<void> {
		this.eventAreaDetails = await this.loadEventGroup(this.groupId);
		// emit the area details back up to the parent component in Views (saving multiple requests for the same data)
		this.$emit("areaDetails", this.eventAreaDetails);
	}

	private async loadSchedules(): Promise<void> {
		if (this.selectedGroup != this.groupId) {
			this.setSelectedGroup(this.groupId);
		}

		this.setEnabledScheduleFilter(true);

		await this.loadScheduleTypes();
	}

	private get showSchedules(): boolean {
		return this.getFeature(["Schedules"]) && !!(this.permissions.canViewSchedules || this.permissions.canEditSchedules);
	}

	private get disableContacts(): boolean {
		return this.getFeature(["Temp_DisableAreaModalContacts"]);
	}

	private get isContactsEnabled(): boolean {
		return this.getFeature(["Contacts"]);
	}

	private get isMaskingEnabled(): boolean {
		return this.getFeature(["Schedules", "Masking"]);
	}

	private get isEventRaiseEnabled(): boolean {
		return this.getFeature(["Schedules", "EventRaise"]);
	}

	private get isEventHistoryEnabled(): boolean {
		return this.getFeature(["Areas", "AreaEventHistory"]) && this.permissions.canViewEventHistory;
	}

	private get isAreaInfoPopupEnabled(): boolean {
		return this.getFeature(["Areas", "AreaDetailsPopout"]);
	}

	private get isAreaInfoPopulateEnabled(): boolean {
		return this.getFeature(["Areas", "AreaDetailsPopout", "AutopopulateEventArea"]);
	}

	private get showHyperlinks(): boolean {
		return this.getFeature(["AreaNotes", "Hyperlinks"]);
	}

	private get isCustomFieldsEnabled(): boolean {
		return this.getFeature(["CustomFields"]);
	}

	public get showAreaNotes() {
		return this.getFeature(["AreaNotes"]);
	}

	private get filteredScheduleTypes(): ScheduleType[] {
		let schedulesTypeList = this.scheduleTypes;

		if (!this.isMaskingEnabled) {
			schedulesTypeList = schedulesTypeList.filter(st => st.scheduleTypeId != ScheduleTypes.Masking);
		}

		if (!this.isEventRaiseEnabled) {
			schedulesTypeList = schedulesTypeList.filter(st => st.scheduleTypeId != ScheduleTypes.EventRaise);
		}

		return schedulesTypeList;
	}

	@Watch("enabledScheduleFilter")
	private onEnabledFilterChanged(disabled: boolean | null): void {
		this.setEnabledScheduleFilter(disabled);
		this.setIsReloadRequired(true);
	}

	@Watch("scheduleSearchFilter")
	private onScheduleSearch(filter: string): void {
		this.updateSearchString(filter);
		this.setIsReloadRequired(true);
	}

	@Watch("eventDetails")
	private async onEventDetailsChangedAutopopulate(eventDetails: EventDetails): Promise<void> {
		if (this.autoPopulate
			&& this.isAreaInfoPopulateEnabled
			&& this.windowed == true
			&& eventDetails
			&& eventDetails.groupID !== this.groupId
		) {
			this.isLoading = true;
			this.eventAreaDetails = eventDetails;
			this.groupId = eventDetails.groupID;
			this.setSelectedGroup(eventDetails.groupID);
			if (this.showSchedules) {
				this.loadSchedules();
			}

			const newUrl = window.location.hash.replace(/\/area-info\/\d+/, `/area-info/${eventDetails.groupID}`);
			window.location.hash = newUrl;

			await this.loadEventAreaDetails();
			this.isLoading = false;
		}
	}

	private onScheduleTypeChanged(newScheduleTypes: number[]): void {
		this.selectedScheduleTypes.forEach(s => {
			this.removeSelectedScheduleType(s);
		});

		newScheduleTypes.forEach(s => {
			this.addSelectedScheduleType(s);
		});

		this.setIsReloadRequired(true);
	}

	private async updateCustomFields(tableTypeId: number): Promise<void> {
		try {
			await this.retrieveCustomFields({
				tableType: tableTypeId, // contact type
				live: true // Get from cache
			});
		} catch (error) {
			console.error("Error retrieving custom fields:", error);
		}
	}
}
